import axios from "axios";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useNav } from "react-router-dom";
import { useForm } from "react-hook-form";
import DatePicker from "react-datepicker";

import CircularProgress from "@material-ui/core/CircularProgress";
import AutocompleteInput from "./AutocompleteInput";
import PopupModal from "./Modal";
import { LessonPlanAPIs } from "../api";
import { useLessonPlans } from "../../../context/LessonPlanProvider";

const periods = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

export default function AddNewLessonPlanModal({
  userId,
  assignedClasses,
  grades,
  preFilled,
  refresh,
}) {
  const { setLessonPlans } = useLessonPlans();
  const history = useHistory();
  const { register, reset, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      createMethod: "manual",
      level: preFilled?.level || "",
      classesTeaching: preFilled?.classesTeaching || "",
    },
  });
  const btnRef = useRef();
  const [popup, setPopup] = useState({});
  const [grade, setGrade] = useState({ grade: "", sections: [] });
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState("");
  const loadTimeoutRef = useRef(null);
  const [resourceData, setResourceData] = useState({
    fromDate: preFilled?.startDate || null,
  });
  const [addContentMessage, setAddContentMessage] = useState("");
  const [addContentLoader, setAddContentLoader] = useState(false);
  // resource data handle
  const handleResourceFromDateChange = (date) => {
    setResourceData({ ...resourceData, fromDate: date });
  };

  const chapterNameValue = watch("chapterName");
  const topicNameValue = watch("topicName");
  const moduleNameValue = watch("moduleName");
  const createMethod = watch("createMethod");

  function getSections(event) {
    reset({
      sectionName: "",
      subjectId: "",
      chapterName: "",
      chapterId: "",
      topicName: "",
      topicId: "",
    });
    setGrade({ name: "", sections: [] });
    setSubjects([]);
    setChapters([]);
    setTopics([]);
    const selectedGrade = event.target.value;
    if (!selectedGrade) return;
    const newSections = grades[selectedGrade];
    setGrade({ grade: selectedGrade, sections: newSections });
  }

  function selectSections(selectedOption) {
    const section = selectedOption.target.value;
    if (section !== "Select Section") getSubjects(section);
  }

  function getSubjects(section) {
    console.log(section);
    reset({
      subjectId: "",
      chapterName: "",
      chapterId: "",
      topicName: "",
      topicId: "",
    });
    setSubjects([]);
    setChapters([]);
    setTopics([]);

    const tempArr = [];
    const selectedSection = `${grade.grade}-${section}`;

    assignedClasses.length &&
      assignedClasses.forEach((assignedClass) => {
        if (assignedClass._id === selectedSection) {
          assignedClass.subjects.forEach((data) => {
            tempArr.push({
              subject: data.subject,
              subjectId: data.subjectId,
            });
          });
        }
      });

    setSubjects([...tempArr]);
  }

  const getChapters = async (e) => {
    reset({
      chapterName: "",
      chapterId: "",
      topicName: "",
      topicId: "",
    });
    setChapters([]);
    setTopics([]);

    const authToken = localStorage.getItem("authToken");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_ORIGIN}/api/v1/subjects/${e.target.value}`,
        {
          headers: {
            token: authToken,
          },
        }
      );

      if (response.data.status === "success") {
        let dt = response.data.data.subject.chapters.filter(
          (item) => !item.disabled
        );
        setChapters(dt);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTopics = useCallback(
    (chapterName) => {
      clearTimeout(loadTimeoutRef.current);
      loadTimeoutRef.current = setTimeout(async () => {
        const chapter = chapters.find((chpt) => chpt.name === chapterName);
        if (!chapter) return;
        setLoading("topic-loading");
        const authToken = localStorage.getItem("authToken");
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_ORIGIN}/api/v1/chapters/${chapter._id}`,
            {
              headers: {
                token: authToken,
              },
            }
          );
          setValue("chapterId", chapter._id);
          if (response.data.status === "success") {
            let dt = response.data.data.chapter.topics.filter(
              (item) => !item.disabled
            );
            setTopics(dt);
          }
        } catch (error) {
          console.log(error);
        }
        setLoading("");
        clearTimeout(loadTimeoutRef.current);
      }, 1000);
    },
    [topics, setTopics, chapters]
  );

  useEffect(async () => {
    if (!chapterNameValue) return;
    setValue("topicName", "");
    setValue("topicId", "");
    setValue("chapterId", "");
    setTopics([]);
    fetchTopics(chapterNameValue);
  }, [chapterNameValue]);

  const submitResourceForm = async (data) => {
    setAddContentMessage("");
    setAddContentLoader(true);

    try {
      const section = assignedClasses.find(
        (sec) => sec._id === `${data.gradeName}-${data.sectionName}`
      );
      if (!section) {
        throw new Error("Please select grade and section");
      }

      if (!resourceData.fromDate) {
        throw new Error("Please start date.");
      }

      if (
        data.createMethod === "attachment" &&
        !data?.attachment &&
        !data.attachment[0]
      )
        throw new Error("Please upload pdf file");

      if (
        !data.subjectId ||
        !data.chapterName ||
        !data.topicName ||
        !data.moduleName
      )
        throw new Error("Please select required field");

      const topic = topics.find((topic) => topic.name === data.topicName);

      const formdata = new FormData();
      const payload = {
        sectionId: section.subjects[0]?.sectionId,
        subjectId: data.subjectId,
        chapterId: data.chapterId || undefined,
        customChapter: !data.chapterId ? data.chapterName : undefined,
        topicId: topic?._id || undefined,
        customTopic: !topic?._id ? data.topicName : undefined,
        customModule: data?.moduleName || undefined,
        startDate: new Date(resourceData.fromDate).getTime(),
        time: data.time,
        createMethod: data.createMethod,
        attachment: data?.attachment && data.attachment[0],
        level: data.level,
        classesTeaching: data.classesTeaching,
      };

      // Append only defined values
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formdata.append(key, value);
        }
      });
      const res = await axios.post(
        LessonPlanAPIs.createLessonPlan(userId),
        formdata,
        {
          headers: {
            token: localStorage.getItem("authToken"),
          },
          validateStatus: () => true,
        }
      );
      if (res.data.status !== "success") throw res.data;
      setLessonPlans((prev) => [res.data.data.lessonPlan, ...prev]);
      setPopup({
        open: true,
        status: "Success",
        message: "Lesson plan created!",
      });

      reset();
      clearOnClose();
      if (refresh) {
        btnRef.current.click();
        refresh();
      } else {
        const action = data.createMethod === "attachment" ? "preview" : "edit";
        const link = `/lessonplans/edit?teacherId=${userId}&startDate=${resourceData.fromDate}&action=${action}`;
        btnRef.current.click();
        history.push(link);
      }
    } catch (error) {
      console.log(error);
      if (error.response?.status === "fail") {
        setAddContentMessage(error.response.message);
      } else {
        setAddContentMessage(error.message || "Try Again.");
      }
    }

    setAddContentLoader(false);
  };

  const clearOnClose = () => {
    setAddContentLoader(false);
    setAddContentMessage("");
    reset();
    setResourceData({ fromDate: null });
  };

  return (
    <Fragment>
      <div
        className="modal fade resourceModel mt-5"
        id="resourceModal"
        tabIndex="-1"
        aria-labelledby="resourceModalLabel"
        data-bs-backdrop="static"
        aria-hidden="true"
      >
        <div className="modal-dialog lp-modal-dailog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title" id="resourceModalLabel">
                CREATE LESSON PLAN
              </h4>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <form onSubmit={handleSubmit(submitResourceForm)}>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-sm">
                    <div className="col-sm">
                      <label htmlFor="s" className="form-label">
                        Period
                      </label>
                      <select
                        className="form-select"
                        id="time"
                        {...register("time")}
                      >
                        <option key={0} value={""}>
                          Select Period
                        </option>
                        {periods.map((period, index) => {
                          return (
                            <option key={index} value={period}>
                              {period}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                  <div
                    className="col-sm"
                    style={{ opacity: preFilled?.level ? 0.6 : 1 }}
                  >
                    <label htmlFor="level" className="mb-2">
                      Level
                    </label>
                    <input
                      type="text"
                      id={"level"}
                      name={"level"}
                      className="form-control form-control-sm"
                      placeholder={"Enter level"}
                      {...register("level")}
                      style={{ fontSize: "1.05rem" }}
                      autoComplete="off"
                      readOnly={preFilled?.level ? true : false}
                    />
                  </div>
                  <div
                    className="col-sm"
                    style={{ opacity: preFilled?.level ? 0.6 : 1 }}
                  >
                    <div className="col-sm">
                      <label htmlFor="classes-teacher" className="form-label">
                        Classes Teaching
                      </label>
                      <input
                        type="text"
                        id={"classes-teaching"}
                        name={"classesTeaching"}
                        className="form-control form-control-sm"
                        {...register("classesTeaching")}
                        style={{ fontSize: "1.05rem" }}
                        autoComplete="off"
                        readOnly={preFilled?.classesTeaching ? true : false}
                      />
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm">
                    <label htmlFor="classSelect" className="form-label">
                      Class
                    </label>
                    <select
                      className="form-select"
                      id="classSelect"
                      {...register("gradeName")}
                      onChange={getSections}
                    >
                      <option defaultValue="">Select Class</option>
                      {Object.keys(grades).length &&
                        Object.keys(grades).map((grade, index) => {
                          return (
                            <option key={index} value={grade}>
                              {grade}
                            </option>
                          );
                        })}
                    </select>
                  </div>

                  <div className="col-sm">
                    <label htmlFor="sectionSelect" className="form-label">
                      Section
                    </label>
                    <select
                      className="form-select"
                      id="sectionSelect"
                      {...register("sectionName")}
                      onChange={selectSections}
                    >
                      <option defaultValue="">Select Section</option>
                      {grade.sections.length &&
                        grade.sections.map((grade, index) => {
                          return (
                            <option key={index} value={grade.section}>
                              {grade.section}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                  <div className="col-sm">
                    <label htmlFor="subjectSelect" className="form-label">
                      Subject
                    </label>
                    <select
                      className="form-select"
                      id="subjectSelect"
                      {...register("subjectId")}
                      onChange={getChapters}
                    >
                      <option defaultValue="" value={""}>
                        Select Subject
                      </option>
                      {subjects.map((subject, index) => (
                        <option key={index} value={subject.subjectId}>
                          {subject.subject}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm">
                    <AutocompleteInput
                      name={"chapterSelect"}
                      label={"Chapter"}
                      placeholder={"Select Chapter"}
                      updateValue={(value) => setValue("chapterName", value)}
                      suggestions={chapters.map((chpt) => ({
                        name: chpt.name,
                        value: chpt._id,
                      }))}
                      value={chapterNameValue}
                      register={register("chapterName")}
                      disabled={loading === "topic-loading"}
                    />
                  </div>
                  <div className="col-sm">
                    <AutocompleteInput
                      name={"topicSelect"}
                      label={"Topic"}
                      placeholder={"Select Topic"}
                      updateValue={(value) => setValue("topicName", value)}
                      suggestions={topics.map((topic) => ({
                        name: topic.name,
                        value: topic._id,
                      }))}
                      value={topicNameValue}
                      register={register("topicName")}
                      disabled={[
                        "subject-loading",
                        "topic-loading",
                        "chapter-loading",
                      ].includes(loading)}
                    />
                  </div>
                  <div className="col-sm">
                    <AutocompleteInput
                      name={"moduleSelect"}
                      label={"Sub Topic"}
                      placeholder={"Enter Sub Topic"}
                      updateValue={(value) => setValue("moduleName", value)}
                      suggestions={[]}
                      value={moduleNameValue}
                      register={register("moduleName")}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  {!preFilled?.startDate ? (
                    <div className="col-sm">
                      <label htmlFor="resourceFromDate" className="mb-2">
                        Date
                      </label>
                      <DatePicker
                        selected={resourceData.fromDate}
                        dateFormat="dd/MM/yyyy"
                        id="resourceFromDate"
                        className="form-control"
                        onChange={handleResourceFromDateChange}
                        placeholderText="From Date"
                      />
                    </div>
                  ) : null}
                  {/*<div className="col-sm">
                    <label htmlFor="createMethod" className="form-label">
                      Manual Create / Upload File
                    </label>
                    <select
                      className="form-select"
                      id="createMethod"
                      {...register("createMethod")}
                    >
                      {[
                        { label: "Create Manually", value: "manual" },
                        { label: "Upload File", value: "attachment" },
                      ].map((type) => (
                        <option key={type.label} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {createMethod === "attachment" ? (
                    <div className="col-sm">
                      <label htmlFor="s" className="form-label">
                        File (PDF)
                      </label>
                      <input
                        className="form-control form-control-sm"
                        type="file"
                        accept="application/pdf"
                        id="files"
                        style={{ fontSize: "1.05rem" }}
                        {...register("attachment")}
                      />
                    </div>
                  ) : null}*/}
                </div>
              </div>

              {addContentMessage && (
                <div className="text-center">
                  <p style={{ color: "#FF075B", fontWeight: "600" }}>
                    {addContentMessage}
                  </p>
                </div>
              )}

              <div className="mb-3 modal_button_container">
                <div className="row">
                  <div className="col d-grid">
                    <button
                      type="button"
                      className="btn resourceModelBtnCancel"
                      data-bs-dismiss="modal"
                      onClick={clearOnClose}
                      ref={btnRef}
                    >
                      Close
                    </button>
                  </div>
                  <div className="col d-grid">
                    {addContentLoader ? (
                      <div className="text-center">
                        <CircularProgress
                          style={{ color: "#FF075B", fontWeight: "600" }}
                        />
                      </div>
                    ) : (
                      <button
                        type="submit"
                        className="btn resourceModelBtnApply"
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <PopupModal
        open={popup.open}
        heading={popup.status}
        message={popup.message}
      >
        <button
          style={{
            color: "#414141",
          }}
          onClick={() => setPopup({})}
        >
          Close
        </button>
      </PopupModal>
    </Fragment>
  );
}
